/**
 * Scalping Trend Strategy — faithful port of MT4 MT4_Scalping_Trend.mq4
 *
 * Uses an ATR-based SuperTrend system for signal generation:
 *
 *   1. ATR SuperTrend — computes upper/lower bands from ATR(period=9) ×
 *      multiplier(4.5) centred on the midPoint (H+L)/2.
 *      - Close > prev upper band → LONG direction
 *      - Close < prev lower band → SHORT direction
 *      - Bands trail: never move against the current direction.
 *
 *   2. Signal on direction flip:
 *      BUY  → Entry = Close, SL = trailing lower band,
 *             Risk = Close − prev Low,
 *             TP1 = Entry + 1.5 × Risk, TP2 = Entry + 3.5 × Risk
 *      SELL → Entry = Close, SL = trailing upper band,
 *             Risk = prev High − Close,
 *             TP1 = Entry − 1.5 × Risk, TP2 = Entry − 3.5 × Risk
 *
 * MT4 features ported:
 *   - Buy/Sell arrow markers (arrow codes 233/234)
 *   - Custom colored candles: Green (long), Red (short), Gray (neutral)
 *   - Trailing stop line (SuperTrend band)
 *   - Target / Stop Loss hit detection with ✓ tick marks
 *   - Live P/L and hit status tracking
 *   - Dashboard metadata for overlay component
 *   - Entry/SL/TP level lines on chart
 *
 * Plots:
 *   - SuperTrend trailing stop curve
 *   - Entry / SL / TP1 / TP2 level lines
 *   - Markers: BUY/SELL arrows + hit markers
 *
 * Category: Strategies | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorMarker, IndicatorPriceLine } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { inferTickSize, roundToTick } from "./price-utils";

// ─── Constants ───────────────────────────────────────────────────
const DIR_NONE = 0;
const DIR_LONG = 1;
const DIR_SHORT = -1;
type Direction = typeof DIR_NONE | typeof DIR_LONG | typeof DIR_SHORT;

// ─── ATR computation (MT4 iATR: simple average of True Range) ────
function computeATR(candles: CandleData[], period: number): number[] {
  const atr: number[] = new Array(candles.length).fill(0);
  if (candles.length < 2) return atr;

  // True Range
  const tr: number[] = [candles[0].high - candles[0].low];
  for (let i = 1; i < candles.length; i++) {
    const h = candles[i].high;
    const l = candles[i].low;
    const pc = candles[i - 1].close;
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }

  // Simple moving average for first period, then Wilder smoothing
  let sum = 0;
  for (let i = 0; i < Math.min(period, tr.length); i++) sum += tr[i];
  atr[period - 1] = sum / period;
  for (let i = period; i < tr.length; i++) {
    atr[i] = (atr[i - 1] * (period - 1) + tr[i]) / period;
  }
  return atr;
}

// ─── Dashboard data interface ────────────────────────────────────
export interface ScalpingTrendDashboard {
  signal: "BUY" | "SELL" | null;
  trend: "LONG" | "SHORT" | "NEUTRAL";
  entry: number | null;
  stopLoss: number | null;
  trailingStop: number | null;
  target1: number | null;
  target2: number | null;
  currentPrice: number;
  livePL: number;
  bookedPL: number;
  scriptName: string;
  status: "Active" | "Target1 Hit" | "Target2 Hit" | "Stop Loss Hit" | "Trailing SL Hit";
  tp1Hit: boolean;
  tp2Hit: boolean;
  slHit: boolean;
  tslHit: boolean;
  /** Candle coloring state for the current bar */
  candleState: "long" | "short" | "neutral";
}

export const scalpingTrendIndicator: IndicatorDefinition = {
  id: "scalping_trend",
  name: "Scalping Trend",
  shortName: "ST",
  category: "strategies",
  pane: "overlay",
  params: [
    { key: "alertEnabled", label: "Alert Notification", type: "boolean", default: true },
    { key: "period", label: "Period", type: "number", default: 9, hidden: true },
    { key: "multiplier", label: "Multiplier", type: "number", default: 4.5, hidden: true },
    { key: "displayLevels", label: "Display Lines", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
    { key: "alertEntry", label: "Alert on Entry", type: "boolean", default: true },
    { key: "alertTP1", label: "Alert on TP1 Hit", type: "boolean", default: true },
    { key: "alertTP2", label: "Alert on TP2 Hit", type: "boolean", default: true },
    { key: "alertSL", label: "Alert on SL Hit", type: "boolean", default: true },
    { key: "lineWidth", label: "Line Width", type: "number", default: 1, min: 1, max: 4, step: 1 },
    { key: "showBackground", label: "Candle Color", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
    { key: "showTrailingStop", label: "Show Trailing SL", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
  ],
  plots: [
    { key: "entry", label: "Entry", color: "#1565C0", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "stopLoss", label: "Stop Loss", color: "#C62828", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target1", label: "Target 1", color: "#2E7D32", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target2", label: "Target 2", color: "#2E7D32", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const period = params.period ?? 9;
    const multiplier = params.multiplier ?? 4.5;
    const displayLevels = params.displayLevels ?? "yes";
    const showTrailingStop = params.showTrailingStop ?? "yes";
    const showBackground = params.showBackground ?? "yes";
    const len = candles.length;
    const minBars = period + 2;

    const emptyResult: PlotData[] = [
      { key: "entry", data: [] },
      { key: "stopLoss", data: [] },
      { key: "target1", data: [] },
      { key: "target2", data: [] },
    ];

    if (len < minBars) return emptyResult;

    const tick = inferTickSize(candles);
    const lw = Math.max(1, Math.min(4, Math.round(params.lineWidth ?? 1)));

    // ════════════════════════════════════════════════════════════════
    // 1. ATR-based SuperTrend Calculation (MT4 OnCalculate logic)
    //    Upper = midPoint + multiplier × ATR
    //    Lower = midPoint − multiplier × ATR
    //    Direction determined by close vs previous bands
    //    Bands trail (never move against position)
    // ════════════════════════════════════════════════════════════════
    const atr = computeATR(candles, period);

    // Raw bands
    const upperRaw: number[] = new Array(len).fill(0);
    const lowerRaw: number[] = new Array(len).fill(0);
    // Trailing bands
    const upper: number[] = new Array(len).fill(0);
    const lower: number[] = new Array(len).fill(0);
    // Direction: 1 = long, -1 = short
    const direction: Direction[] = new Array(len).fill(DIR_LONG);
    // SuperTrend line values (trailing stop)
    const longTS: number[] = new Array(len).fill(0);
    const shortTS: number[] = new Array(len).fill(0);

    for (let i = 0; i < len; i++) {
      const midPoint = (candles[i].high + candles[i].low) / 2.0;
      upperRaw[i] = midPoint + multiplier * atr[i];
      lowerRaw[i] = midPoint - multiplier * atr[i];
    }

    // Initialize first bar
    upper[0] = upperRaw[0];
    lower[0] = lowerRaw[0];
    direction[0] = DIR_LONG;

    // MT4 processes from oldest to newest (Bars-1 down to 0 in MT4 = index 0..len-1 here)
    for (let i = 1; i < len; i++) {
      const close = candles[i].close;

      // Determine direction
      if (close > upper[i - 1]) {
        direction[i] = DIR_LONG;
      } else if (close < lower[i - 1]) {
        direction[i] = DIR_SHORT;
      } else {
        direction[i] = direction[i - 1];
      }

      // Trail lower band (only moves up during LONG)
      lower[i] = lowerRaw[i];
      if (direction[i] === DIR_LONG && lower[i] < lower[i - 1]) {
        lower[i] = lower[i - 1];
      }

      // Trail upper band (only moves down during SHORT)
      upper[i] = upperRaw[i];
      if (direction[i] === DIR_SHORT && upper[i] > upper[i - 1]) {
        upper[i] = upper[i - 1];
      }

      // Reset bands on direction change (MT4 logic)
      const prevDir = direction[i - 1];
      if (direction[i] === DIR_SHORT && prevDir === DIR_LONG) {
        upper[i] = upperRaw[i]; // reset upper on flip to short
      }
      if (direction[i] === DIR_LONG && prevDir === DIR_SHORT) {
        lower[i] = lowerRaw[i]; // reset lower on flip to long
      }

      // Store trailing stop values
      if (direction[i] === DIR_LONG) {
        longTS[i] = lower[i];
      } else {
        shortTS[i] = upper[i];
      }
    }

    // ════════════════════════════════════════════════════════════════
    // 2. Signal Generation (MT4: direction flip detection)
    //    BUY when direction flips from SHORT→LONG
    //    SELL when direction flips from LONG→SHORT
    //    TP1 = 1.5 × risk, TP2 = 3.5 × risk
    // ════════════════════════════════════════════════════════════════
    const markers: IndicatorMarker[] = [];

    // Signal tracking arrays
    const SIGNAL: Direction[] = new Array(len).fill(DIR_NONE);
    const ENTRY: number[] = new Array(len).fill(0);
    const SL: number[] = new Array(len).fill(0);
    const TSL: number[] = new Array(len).fill(0);
    const TP1: number[] = new Array(len).fill(0);
    const TP2: number[] = new Array(len).fill(0);
    // Candle color state
    const candleColorState: ("long" | "short" | "neutral")[] = new Array(len).fill("neutral");

    let lastSignalDir: Direction = DIR_NONE;
    let lastSignalBar = -1;
    const arrowMarkers: { time: number; direction: "long" | "short"; price: number }[] = [];

    // Process only closed bars (skip forming bar like MT4)
    const lastClosedBar = len - 2;

    for (let i = 1; i <= lastClosedBar; i++) {
      const curDir = direction[i];
      const prevDir = direction[i - 1];

      // Detect direction flip → new signal
      const isBuySignal = curDir === DIR_LONG && prevDir === DIR_SHORT;
      const isSellSignal = curDir === DIR_SHORT && prevDir === DIR_LONG;

      if (isBuySignal) {
        SIGNAL[i] = DIR_LONG;
        ENTRY[i] = roundToTick(candles[i].close, tick);
        // Targets: original candle-range based (1.5× and 3.5×)
        const candleRiskL = Math.max(candles[i].close - candles[i - 1].low, tick);
        TP1[i] = roundToTick(ENTRY[i] + 1.5 * candleRiskL, tick);
        TP2[i] = roundToTick(ENTRY[i] + 3.5 * candleRiskL, tick);
        // SL = same distance as TGT-1 (1:1 R:R), TSL starts there
        SL[i] = roundToTick(ENTRY[i] - (TP1[i] - ENTRY[i]), tick);
        TSL[i] = SL[i];

        lastSignalDir = DIR_LONG;
        lastSignalBar = i;

        markers.push({
          time: candles[i].time,
          position: "belowBar",
          color: "#2E7D32",
          shape: "arrowUp",
          text: "",
          size: 2,
        });
        arrowMarkers.push({ time: candles[i].time, direction: "long" as const, price: candles[i].low });
      } else if (isSellSignal) {
        SIGNAL[i] = DIR_SHORT;
        ENTRY[i] = roundToTick(candles[i].close, tick);
        // Targets: original candle-range based (1.5× and 3.5×)
        const candleRiskS = Math.max(candles[i - 1].high - candles[i].close, tick);
        TP1[i] = roundToTick(ENTRY[i] - 1.5 * candleRiskS, tick);
        TP2[i] = roundToTick(ENTRY[i] - 3.5 * candleRiskS, tick);
        // SL = same distance as TGT-1 (1:1 R:R), TSL starts there
        SL[i] = roundToTick(ENTRY[i] + (ENTRY[i] - TP1[i]), tick);
        TSL[i] = SL[i];

        lastSignalDir = DIR_SHORT;
        lastSignalBar = i;

        markers.push({
          time: candles[i].time,
          position: "aboveBar",
          color: "#800000",
          shape: "arrowDown",
          text: "",
          size: 2,
        });
        arrowMarkers.push({ time: candles[i].time, direction: "short" as const, price: candles[i].high });
      } else {
        // No new signal — carry forward
        SIGNAL[i] = lastSignalDir;
        ENTRY[i] = i > 0 ? ENTRY[i - 1] : 0;
        SL[i] = i > 0 ? SL[i - 1] : 0;
        TP1[i] = i > 0 ? TP1[i - 1] : 0;
        TP2[i] = i > 0 ? TP2[i - 1] : 0;
      }

      // Update trailing stop — ratchet-only: never widen beyond previous TSL.
      // Skip on the signal bar itself (TSL was already seeded from SL above).
      // LONG:  TSL only moves UP   (max), never falls below the locked value.
      // SHORT: TSL only moves DOWN (min), never rises above the locked value.
      if (!isBuySignal && !isSellSignal) {
        if (lastSignalDir === DIR_LONG && longTS[i] > 0) {
          const prevTSL = i > 0 ? TSL[i - 1] : 0;
          TSL[i] = Math.max(prevTSL, roundToTick(longTS[i], tick));
        } else if (lastSignalDir === DIR_SHORT && shortTS[i] > 0) {
          const prevTSL = i > 0 ? TSL[i - 1] : 0;
          TSL[i] = prevTSL > 0
            ? Math.min(prevTSL, roundToTick(shortTS[i], tick))
            : roundToTick(shortTS[i], tick);
        } else {
          TSL[i] = i > 0 ? TSL[i - 1] : 0;
        }
      }

      // Candle coloring (MT4: setCandleColor)
      if (lastSignalDir === DIR_LONG) {
        candleColorState[i] = "long";
      } else if (lastSignalDir === DIR_SHORT) {
        candleColorState[i] = "short";
      } else {
        candleColorState[i] = "neutral";
      }
    }

    // Forward-fill to forming bar
    if (lastClosedBar >= 0 && lastClosedBar < len - 1) {
      for (let i = lastClosedBar + 1; i < len; i++) {
        SIGNAL[i] = SIGNAL[lastClosedBar];
        ENTRY[i] = ENTRY[lastClosedBar];
        SL[i] = SL[lastClosedBar];
        TSL[i] = TSL[lastClosedBar];
        TP1[i] = TP1[lastClosedBar];
        TP2[i] = TP2[lastClosedBar];
        candleColorState[i] = candleColorState[lastClosedBar];
      }
    }

    // ════════════════════════════════════════════════════════════════
    // 3. Target / SL / Trailing SL hit detection (MT4: Trade::Evaluate)
    // ════════════════════════════════════════════════════════════════
    let highSinceSignal = 0;
    let lowSinceSignal = Infinity;
    let status: ScalpingTrendDashboard["status"] = "Active";
    let tp1Hit = false;
    let tp2Hit = false;
    let slHit = false;
    let tslHit = false;
    let bookedPL = 0;

    if (lastSignalBar >= 0) {
      // Include live bar (len-1) so status updates in real-time on price touch
      for (let i = lastSignalBar; i < len; i++) {
        highSinceSignal = Math.max(highSinceSignal, candles[i].high);
        lowSinceSignal = Math.min(lowSinceSignal, candles[i].low);
      }

      const entryVal = ENTRY[lastSignalBar];
      const slVal = SL[lastSignalBar];
      const tp1Val = TP1[lastSignalBar];
      const tp2Val = TP2[lastSignalBar];

      if (SIGNAL[lastSignalBar] === DIR_LONG) {
        if (highSinceSignal >= tp2Val) {
          status = "Target2 Hit";
          tp1Hit = true;
          tp2Hit = true;
          bookedPL = tp2Val - entryVal;
        } else if (highSinceSignal >= tp1Val) {
          status = "Target1 Hit";
          tp1Hit = true;
          bookedPL = tp1Val - entryVal;
        }
        if (lowSinceSignal <= slVal) {
          status = "Stop Loss Hit";
          slHit = true;
          bookedPL = slVal - entryVal;
        }
        // Check trailing SL (include live bar for real-time status)
        for (let i = lastSignalBar + 1; i < len; i++) {
          if (candles[i].low <= TSL[i] && TSL[i] > slVal) {
            tslHit = true;
            if (!slHit) {
              status = "Trailing SL Hit";
              bookedPL = TSL[i] - entryVal;
            }
            break;
          }
        }
      } else if (SIGNAL[lastSignalBar] === DIR_SHORT) {
        if (lowSinceSignal <= tp2Val) {
          status = "Target2 Hit";
          tp1Hit = true;
          tp2Hit = true;
          bookedPL = entryVal - tp2Val;
        } else if (lowSinceSignal <= tp1Val) {
          status = "Target1 Hit";
          tp1Hit = true;
          bookedPL = entryVal - tp1Val;
        }
        if (highSinceSignal >= slVal) {
          status = "Stop Loss Hit";
          slHit = true;
          bookedPL = entryVal - slVal;
        }
        // Check trailing SL (include live bar for real-time status)
        for (let i = lastSignalBar + 1; i < len; i++) {
          if (candles[i].high >= TSL[i] && TSL[i] < slVal) {
            tslHit = true;
            if (!slHit) {
              status = "Trailing SL Hit";
              bookedPL = entryVal - TSL[i];
            }
            break;
          }
        }
      }
    }

    // ════════════════════════════════════════════════════════════════
    // 4. Dashboard metadata (matching MT4 Panel class fields)
    // ════════════════════════════════════════════════════════════════
    const uiBar = Math.max(0, len - 2);
    const currentPrice = candles[len - 1]?.close ?? 0;
    const currSignal = SIGNAL[uiBar];
    const currEntry = ENTRY[uiBar];
    const currSL = SL[uiBar];
    const currTSL = TSL[uiBar];
    const currTP1 = TP1[uiBar];
    const currTP2 = TP2[uiBar];

    let dashSignal: ScalpingTrendDashboard["signal"] = null;
    let dashTrend: ScalpingTrendDashboard["trend"] = "NEUTRAL";
    let livePL = 0;

    if (currSignal !== DIR_NONE && currEntry > 0) {
      if (currSignal === DIR_LONG) {
        dashSignal = "BUY";
        dashTrend = "LONG";
        livePL = currentPrice - currEntry;
      } else {
        dashSignal = "SELL";
        dashTrend = "SHORT";
        livePL = currEntry - currentPrice;
      }
    }

    const dashboard: ScalpingTrendDashboard = {
      signal: dashSignal,
      trend: dashTrend,
      entry: currEntry > 0 ? currEntry : null,
      stopLoss: currSL > 0 ? currSL : null,
      trailingStop: currTSL > 0 ? currTSL : null,
      target1: currTP1 > 0 ? currTP1 : null,
      target2: currTP2 > 0 ? currTP2 : null,
      currentPrice,
      livePL,
      bookedPL,
      scriptName: "",
      status,
      tp1Hit,
      tp2Hit,
      slHit,
      tslHit,
      candleState: candleColorState[uiBar] || "neutral",
    };

    // ════════════════════════════════════════════════════════════════
    // 5. Build level line series + candle colors + hit markers
    // ════════════════════════════════════════════════════════════════
    const entryData: { time: number; value: number }[] = [];
    const slData: { time: number; value: number }[] = [];
    const t1Data: { time: number; value: number }[] = [];
    const t2Data: { time: number; value: number }[] = [];

    // Plot series intentionally left empty — priceLines handle all level drawing.

    // Build candle color map (MT4: Green for long, Red for short, Gray for neutral)
    const candleColors: { time: number; color: string }[] = [];
    if (showBackground !== "no") for (let i = 0; i < len; i++) {
      const state = candleColorState[i];
      if (state === "long") {
        candleColors.push({ time: candles[i].time, color: "#2E7D32" }); // Green (matching dashboard)
      } else if (state === "short") {
        candleColors.push({ time: candles[i].time, color: "#800000" }); // Maroon (matching dashboard)
      } else {
        candleColors.push({ time: candles[i].time, color: "#9E9E9E" }); // Neutral grey
      }
    }

    // ── Hit markers: place on the ACTUAL bar where target/SL was hit ──
    if (lastSignalBar >= 0 && (tp1Hit || tp2Hit || slHit)) {
      const sigDir = SIGNAL[lastSignalBar];
      const entryVal = ENTRY[lastSignalBar];
      const slVal = SL[lastSignalBar];
      const tp1Val = TP1[lastSignalBar];
      const tp2Val = TP2[lastSignalBar];
      let _tp1Hit = tp1Hit, _tp2Hit = tp2Hit, _slHit = slHit;

      for (let i = lastSignalBar + 1; i <= Math.min(lastClosedBar, len - 1); i++) {
        if (sigDir === DIR_LONG) {
          if (_tp1Hit && candles[i].high >= tp1Val) {
            markers.push({
              time: candles[i].time,
              position: "aboveBar",
              shape: "circle",
              color: "#2E7D32",
              text: "✓ T1",
              size: 1,
            });
            _tp1Hit = false;
          }
          if (_tp2Hit && candles[i].high >= tp2Val) {
            markers.push({
              time: candles[i].time,
              position: "aboveBar",
              shape: "circle",
              color: "#2E7D32",
              text: "✓ T2",
              size: 1,
            });
            _tp2Hit = false;
          }
          if (_slHit && candles[i].low <= slVal) {
            markers.push({
              time: candles[i].time,
              position: "belowBar",
              shape: "circle",
              color: "#C62828",
              text: "✗ SL",
              size: 1,
            });
            _slHit = false;
          }
        } else if (sigDir === DIR_SHORT) {
          if (_tp1Hit && candles[i].low <= tp1Val) {
            markers.push({
              time: candles[i].time,
              position: "belowBar",
              shape: "circle",
              color: "#2E7D32",
              text: "✓ T1",
              size: 1,
            });
            _tp1Hit = false;
          }
          if (_tp2Hit && candles[i].low <= tp2Val) {
            markers.push({
              time: candles[i].time,
              position: "belowBar",
              shape: "circle",
              color: "#2E7D32",
              text: "✓ T2",
              size: 1,
            });
            _tp2Hit = false;
          }
          if (_slHit && candles[i].high >= slVal) {
            markers.push({
              time: candles[i].time,
              position: "aboveBar",
              shape: "circle",
              color: "#C62828",
              text: "✗ SL",
              size: 1,
            });
            _slHit = false;
          }
        }
      }
    }

    // ── Badge price-line labels for axis (Entry / SL / T1 / T2) ──
    // lineVisible:true → horizontal line extends to right edge of chart
    // axisLabelVisible:true → colored badge label on the price axis
    const priceLines: IndicatorPriceLine[] = [];
    if (displayLevels !== "no" && currEntry > 0 && lastSignalBar >= 0) {
      const entryTime = candles[lastSignalBar].time;
      priceLines.push(
        { id: "st-entry", price: currEntry, color: "#1565C0", title: "Entry",     lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "st-sl",    price: currSL,    color: "#C62828", title: "Stop Loss", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "st-t1",   price: currTP1,   color: "#2E7D32", title: "Target_1",  lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "st-t2",   price: currTP2,   color: "#2E7D32", title: "Target_2",  lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
      );
      if (showTrailingStop !== "no" && currTSL > 0) {
        priceLines.push(
          { id: "st-tsl", price: currTSL, color: "#E65100", title: "Trailing SL", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        );
      }
    }

    return [
      { key: "entry", data: entryData, markers, priceLines, metadata: { dashboard, candleColors, arrowMarkers } },
      { key: "stopLoss", data: slData },
      { key: "target1", data: t1Data },
      { key: "target2", data: t2Data },
    ];
  },
};
